import { throwIfAborted } from './abort.js';
import { isDepositOperation, OnrampApiError, } from './client.js';
import { directPurchaseHeaders, } from './direct-purchase/credentials.js';
export function createIntentClient(transport, assertActive) {
    const renewals = new Map();
    return {
        async getDirectPurchaseContext(input, surface, signal, credentials) {
            const result = await transport.GET('/v1/onramp/direct-token-purchase/context', {
                headers: directPurchaseHeaders(credentials),
                params: {
                    query: { marketAddress: input.marketAddress, outcomeIndex: input.outcomeIndex, surface },
                },
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
        async verifyRequiredAction(id, customerId, accessToken, signal) {
            const result = await transport.POST('/v1/onramp/operations/{id}/verification', {
                params: { path: { id } },
                body: { customerId, accessToken },
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
        async getContext(signal, surface) {
            const result = await transport.GET('/v1/onramp/context', {
                ...(surface ? { params: { query: { surface } } } : {}),
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
        async createOperation(input, signal, credentials) {
            const result = await transport.POST('/v1/onramp/operations', {
                headers: directPurchaseHeaders(credentials),
                body: input,
                ...(signal ? { signal } : {}),
            });
            assertActive();
            const operation = unwrap(result);
            if (!isDepositOperation(operation))
                throw new Error('onramp_operation_method_mismatch');
            return operation;
        },
        async prepareAttempt(id, signature, expiresAt, sessionProofId, signal, amounts, providerPaymentMethodId, credentials) {
            const result = await transport.POST('/v1/onramp/operations/{id}/attempts', {
                headers: directPurchaseHeaders(credentials),
                params: { path: { id } },
                body: {
                    signature,
                    expiresAt,
                    sessionProofId,
                    ...(amounts ? { amounts } : {}),
                    ...(providerPaymentMethodId ? { providerPaymentMethodId } : {}),
                },
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
        async refreshAttemptQuote(id, attemptId, quoteSignature, quoteExpiresAt, sessionProofId, signal, amounts, amountUnits, credentials) {
            const previous = renewals.get(attemptId);
            const binding = (async () => {
                await previous;
                assertActive();
                if (signal)
                    throwIfAborted(signal);
                // Once sent, finish observing the write even if typing invalidates this quote.
                // The next revision waits for it, preventing an older request overwriting a newer one.
                const result = await transport.POST('/v1/onramp/operations/{id}/attempts/{attemptId}/quote', {
                    headers: directPurchaseHeaders(credentials),
                    params: { path: { id, attemptId } },
                    body: {
                        quoteSignature,
                        quoteExpiresAt,
                        sessionProofId,
                        ...(amounts ? { amounts } : {}),
                        ...(amountUnits !== undefined ? { amountUnits } : {}),
                    },
                });
                assertActive();
                return unwrap(result);
            })();
            const settled = binding.then(() => undefined, () => undefined);
            renewals.set(attemptId, settled);
            try {
                return await binding;
            }
            finally {
                if (renewals.get(attemptId) === settled)
                    renewals.delete(attemptId);
            }
        },
        async cancelOperation(id, signal) {
            const result = await transport.POST('/v1/onramp/operations/{id}/cancel', {
                params: { path: { id } },
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
        async reportPaymentEvent(id, attemptId, providerTransactionId, signal) {
            const result = await transport.POST('/v1/onramp/operations/{id}/attempts/{attemptId}/report', {
                params: { path: { id, attemptId } },
                body: { providerTransactionId },
                ...(signal ? { signal } : {}),
            });
            assertActive();
            return unwrap(result);
        },
    };
}
export function unwrap(result) {
    if (result.data)
        return result.data.data;
    const retry = result.response.headers.get('Retry-After');
    const delay = retry
        ? /^\d+$/.test(retry)
            ? Number(retry) * 1000
            : Date.parse(retry) - Date.now()
        : 0;
    throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, Number.isFinite(delay) ? Math.max(0, Math.min(delay, 2147483647)) : 0, result.response.status === 422 &&
        result.error?.error.code === 'SYSTEM_VALIDATION_ERROR' &&
        result.error.error.details?.reason === 'onramp_session_refresh_required'
        ? 'refresh_session'
        : undefined, typeof result.error?.error.details?.reason === 'string' &&
        [
            'exchange_review_changed',
            'exchange_review_expired',
            'exchange_amount_precision',
            'exchange_verification_rejected',
            'exchange_authorization_expired',
            'exchange_connection_disconnected',
        ].includes(result.error.error.details.reason)
        ? result.error.error.details.reason
        : undefined);
}
